import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraSelect } from "../../../components/AtiraSelect";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { Modal } from "../../../components/modal/Modal";
import { EntryKind } from "../../../model/entry/types/EntryKind.enum";
import { Spacing } from "../../../theme/Spacing";

const Container = styled(Flex)`
  width: 100%;
  gap: ${Spacing.m};
  justify-content: center;
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  margin: 0;
  margin-top: ${Spacing.m};
  height: 2.3rem;
`;

type EntryBulkConvertModalProps = {
  isOpen: boolean;
  onClose: VoidFunction;
  onSubmit: ATVoidFunction<EntryKind>;
  loading: boolean;
  serverError: TypedCRUDError | null;
  /** The kinds every selected entry can be converted to. */
  availableKinds: EntryKind[];
};

export const EntryBulkConvertModal: React.FC<EntryBulkConvertModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  serverError,
  availableKinds,
}) => {
  const [pickedKind, setPickedKind] = useState<EntryKind | undefined>(
    undefined,
  );

  const { t } = useTranslation();

  // The selection can change between openings, so a previous pick may no
  // longer be a kind every selected entry can convert to.
  const entryKind =
    pickedKind && availableKinds.includes(pickedKind) ? pickedKind : undefined;

  const getDropDownOptions = () => {
    return availableKinds
      .filter((k) => k !== entryKind)
      .map((x) => ({
        label: t(`entries.table.entry.${x.toLowerCase()}`),
        value: x,
      }));
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={t("entries.table.entry.convert.bulk.title")}
    >
      <Container>
        <Text color="darkerSub">
          {t("entries.table.entry.convert.bulk.subtitle")}
        </Text>

        <AtiraSelect
          options={getDropDownOptions()}
          value={{
            label: t(
              `entries.table.entry.${entryKind?.toLowerCase() || "kind.select"}`,
            ),
            value: entryKind,
          }}
          onChange={(e: any) => setPickedKind(e)}
        />

        {serverError?.message ? (
          <Text color="danger" fontSize="s">
            {t(serverError.message)}
          </Text>
        ) : null}

        <StyledButton
          loading={loading}
          disabled={!entryKind}
          onClick={() => onSubmit(entryKind!)}
        >
          {t("entries.table.entry.convert.update.button")}
        </StyledButton>
      </Container>
    </Modal>
  );
};
